import { useState, useEffect } from 'react';
import { fmtAmount, fmtDate, PartyAvatar } from '../lib/helpers';
import { AddTransactionModal } from '../components/AddTransactionModal';
import { AddPartyModal } from '../components/AddPartyModal';
import { api } from '../lib/api';

export function PartyDetailPage({ party, onBack, onUpdate, onDelete, onAddTxn, onDelTxn }) {
  const [txns, setTxns]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [txnModal, setTxnModal] = useState(null); // 'credit' | 'debit' | null
  const [editModal, setEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadTxns = async () => {
    setLoading(true);
    try { setTxns(await api.getTransactions(party.id)); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadTxns(); }, [party.id]);

  const totalCredit = txns.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);
  const totalDebit  = txns.filter(t => t.type === 'debit').reduce((s, t) => s + t.amount, 0);
  const balance     = totalCredit - totalDebit;

  const handleAddTxn = async (data) => {
    await onAddTxn(data);
    await loadTxns();
  };
  const handleDelTxn = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await onDelTxn(id);
    await loadTxns();
  };
  const handleUpdateParty = async (data) => {
    await onUpdate(party.id, data);
    setEditModal(false);
  };
  const handleDeleteParty = async () => {
    await onDelete(party.id);
    onBack();
  };

  const sendReminder = () => {
    const msg = `Hello ${party.name}, you have a pending payment of ${fmtAmount(balance)}. Please clear it at your earliest. Thank you!`;
    const phone = party.phone?.replace(/\D/g,'');
    if (phone) {
      window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`);
    } else {
      alert('Reminder message:\n\n' + msg + '\n\n(Add phone number to send via WhatsApp)');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="party-header">
        <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 16 }}>
          ← Back
        </button>
        <div className="party-header-avatar">
          {party.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="party-header-name">{party.name}</div>
          <div className="party-header-phone">
            {party.phone ? `📞 ${party.phone}` : 'No phone'}
            &nbsp;·&nbsp;
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '1px 7px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
              {party.type}
            </span>
          </div>
        </div>
        <div className="party-header-bal">
          <div className="party-header-bal-amount">{fmtAmount(Math.abs(balance))}</div>
          <div className="party-header-bal-label">
            {Math.abs(balance) < 0.01 ? 'Settled' : balance > 0 ? '📥 Will Get' : '📤 Will Give'}
          </div>
        </div>
      </div>

      {/* Balance strip */}
      <div className="balance-strip">
        <div className="bal-strip-item">
          <div className="bal-strip-label">Total Credit</div>
          <div className="bal-strip-amount" style={{ color: 'var(--green-dark)' }}>{fmtAmount(totalCredit)}</div>
        </div>
        <div className="bal-strip-item">
          <div className="bal-strip-label">Total Debit</div>
          <div className="bal-strip-amount" style={{ color: 'var(--red-dark)' }}>{fmtAmount(totalDebit)}</div>
        </div>
        <div className="bal-strip-item">
          <div className="bal-strip-label">Entries</div>
          <div className="bal-strip-amount">{txns.length}</div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="txn-actions">
        <button className="txn-action-btn credit" onClick={() => setTxnModal('credit')}>
          📥 Credit (Will Get)
        </button>
        <button className="txn-action-btn debit" onClick={() => setTxnModal('debit')}>
          📤 Debit (Will Give)
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        {balance > 0 && (
          <button className="remind-btn" onClick={sendReminder}>
            💬 Send Reminder
          </button>
        )}
        <button className="btn btn-sm" onClick={() => setEditModal(true)}>✏️ Edit</button>
        <button className="btn btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
          onClick={() => setShowDeleteConfirm(true)}>🗑 Delete</button>
      </div>

      {/* Transactions */}
      <div className="content">
        <div style={{ padding: '12px 16px' }}>
          {loading ? (
            <div className="empty"><div className="loading-spin" style={{ margin: '0 auto 8px' }} />Loading…</div>
          ) : txns.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📋</div>
              No transactions yet.<br />
              <span style={{ fontSize: 12 }}>Add Credit or Debit entry above</span>
            </div>
          ) : (
            <div className="txn-list">
              {txns.map((t, i) => {
                // Running balance up to this point (from oldest)
                const ordered = [...txns].reverse();
                const idx = ordered.findIndex(x => x.id === t.id);
                const runBal = ordered.slice(0, idx + 1).reduce((s, x) =>
                  s + (x.type === 'credit' ? x.amount : -x.amount), 0);

                return (
                  <div key={t.id} className="txn-row">
                    <div className={`txn-dot ${t.type}`}>
                      {t.type === 'credit' ? '📥' : '📤'}
                    </div>
                    <div className="txn-info">
                      <div className="txn-note">{t.note || (t.type === 'credit' ? 'Credit entry' : 'Debit entry')}</div>
                      <div className="txn-date">{fmtDate(t.date)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
                        Balance: <span style={{ color: runBal >= 0 ? 'var(--green-dark)' : 'var(--red-dark)', fontWeight: 600 }}>
                          {fmtAmount(runBal)}
                        </span>
                      </div>
                    </div>
                    <div className="txn-right">
                      <div className={`txn-amount ${t.type}`}>{fmtAmount(t.amount)}</div>
                      <div className={`txn-label ${t.type}`}>{t.type === 'credit' ? 'Credit' : 'Debit'}</div>
                    </div>
                    <button className="delete-btn" onClick={() => handleDelTxn(t.id)} title="Delete entry">🗑</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {txnModal && (
        <AddTransactionModal type={txnModal} partyId={party.id} partyName={party.name}
          onSave={handleAddTxn} onClose={() => setTxnModal(null)} />
      )}
      {editModal && (
        <AddPartyModal existing={party} onSave={handleUpdateParty} onClose={() => setEditModal(false)} />
      )}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">⚠️ Delete Party?</span>
              <button className="close-btn" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, color: 'var(--text-mid)' }}>
                Delete <strong>{party.name}</strong> and all their transactions? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button className="btn btn-red" style={{ flex: 1, justifyContent: 'center' }} onClick={handleDeleteParty}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
